/**
 * Lazy Video Module
 * Defers a <video>'s download until it is actually in view. The markup keeps
 * the URL on <source data-src> with preload="none", so nothing is fetched on
 * page load; the src is set once part of the video enters the viewport (or
 * slides into a carousel's visible area — .slick-list's overflow clips it
 * until then). Slick clones (.slick-cloned) are never observed, so the
 * carousel's hidden copy never downloads. [UX-21388]
 * @module lazyVideo
 */

import $ from 'jquery';

const loadVideo = (video) => {
  const source = video.querySelector('source[data-src]');
  if (!source) return;
  source.src = source.dataset.src;
  source.removeAttribute('data-src');
  video.preload = 'metadata';
  video.load();
};

let observer;
const getObserver = () => {
  if (!observer) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // An off-screen slide sitting right next to the visible ones reports
        // isIntersecting with a 0 ratio; wait until some of it is showing.
        if (entry.intersectionRatio > 0) {
          observer.unobserve(entry.target);
          loadVideo(entry.target);
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });
  }
  return observer;
};

/**
 * Starts watching a <video> and loads its source when it comes into view.
 * @param {HTMLVideoElement} video - video whose <source> has data-src
 */
export default function lazyVideo(video) {
  if ($(video).closest('.slick-cloned').length) return;

  if (!('IntersectionObserver' in window)) {
    loadVideo(video);
    return;
  }

  getObserver().observe(video);
  // jQuery's trigger('play') (gallery afterChange) runs handlers before the
  // native play(), so an autoplay that beats the observer still has a source.
  $(video).on('play.lazyVideo', () => loadVideo(video));
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/abtest/lazy-video.js